<template>
  <el-dialog
    :visible.sync="visible"
    title="选择租赁方案"
    width="1080px"
    :before-close="handleClose"
    :append-to-body="true"
    :destroy-on-close="true"
    @open="handleOpen"
  >
    <div class="dialog-content">
      <el-alert
        title="最多可选6个租车方案，暂不支持其他类型。"
        type="info"
        :closable="false"
      >
      </el-alert>

      <SelectionTable
        v-if="visible"
        ref="selectionTableRef"
        v-model="selectedPlans"
        :columns="columns"
        :data="tableData"
        :loading="loading"
        :pagination="pagination"
        :max-count="6"
        @change="handleTableChange"
      >
        <template #search>
          <div class="search-area">
            <div class="search-form">
              <div class="form-item">
                <span class="label">租赁方案</span>
                <QS-select
                  v-model="rentPeriodType"
                  :optionData="rentPeriodTypeOptions"
                  :optionConfig="{
                    valueKey: 'value',
                    labelKey: 'label',
                  }"
                  filterable
                  placeholder="请选择"
                />
                <el-button type="primary" class="search-btn" @click="handleSearch"
                  >查询</el-button
                >
              </div>
            </div>
          </div>
        </template>
        <template #extra>
          <div class="add-plan">
            <span class="text">没有适合的租赁方案，</span>
            <el-button type="text" class="action" @click="handleAddPlan"
              >点击新建</el-button
            >
          </div>
        </template>
      </SelectionTable>
    </div>

    <template #footer>
      <div class="footer">
        <el-button @click="handleClose">取消</el-button>
        <el-button
          type="primary"
          @click="handleConfirm"
          :disabled="selectedPlans.length === 0"
          >确定</el-button
        >
      </div>
    </template>
  </el-dialog>
</template>

<script setup>
import { getPlanListByGoods } from '@/api/domain/shop'
import SelectionTable from '@/components/SelectionTable'
import { use$to, communicationHandler } from '@/utils/useUtils'
import { Tooltip } from '@qs/ui'
import { computed, ref } from 'vue'
import { getSinglePlan } from '@/api/goods'

const props = defineProps({
  cityCode: {
    type: String,
    default: '',
  },
  modelId: {
    type: [Number, String],
    default: 0,
  },
  brandId: {
    type: [Number, String],
    default: 0,
  },
  seriesId: {
    type: [Number, String],
    default: 0,
  },
})

const $to = use$to()
const emits = defineEmits(['confirm'])

const selectionTableRef = ref(null)
const visible = ref(false)
const loading = ref(false)
const selectedPlans = ref([])
const tableData = ref([])
const rentPeriodType = ref(3)

const pagination = ref({
  pageNum: 1,
  pageSize: 10,
  total: 0,
})

// 编辑的方案相关信息
const editSchemeInfo = ref({
  id: null,
})

// 租赁周期选项
const rentPeriodTypeOptions = [
  { label: '日租', value: 1 },
  { label: '月租', value: 3 },
]

// 表格列配置
const columns = computed(() => {
  return [
    {
      name: 'id',
      columnProps: {
        prop: 'id',
        label: '租赁方案ID',
      },
    },
    {
      name: 'name',
      columnProps: {
        prop: 'name',
        label: '租赁方案名称',
      },
    },
    {
      name: 'rentPeriod',
      columnProps: {
        prop: 'rentPeriod',
        label: '租期',
      },
      render: ({ row }) => {
        const { rentPeriod, rentPeriodType: rowPeriodType, rentPeriodTypeName } = row
        if (!rentPeriod) return '--'
        if (rowPeriodType === 1) {
          return `${rentPeriod}天`
        }
        return `${rentPeriod}个${rentPeriodTypeName}`
      },
    },
    {
      name: 'deposit',
      columnProps: {
        prop: 'deposit',
        label: '押金总额',
      },
      render: ({ row }) => {
        if (!row.deposit) return '--'
        return `${row.deposit}元`
      },
    },
    {
      name: 'monthlyRent',
      columnProps: {
        prop: 'monthlyRent',
        label: '租金',
      },
      render: ({ row }) => {
        if (!row.rentAmount) return '--'
        return `${row.rentAmount}元/${row.rentAmountUnit}`
      },
    },
    {
      name: 'otherFeeList',
      columnProps: {
        prop: 'otherFeeList',
        label: '其他费项',
      },
      render: ({ row }) => {
        if (!row.otherFeeList || row.otherFeeList?.length === 0) return '--'
        if (row.otherFeeList?.length === 1) {
          return row.otherFeeList?.[0]
        }
        return (
          <div>
            <span>{row.otherFeeList?.[0]}</span>
            <Tooltip
              effect="dark"
              content={row.otherFeeList?.join('、')}
              placement="top"
            >
              <span class="a-hover-link">等{row.otherFeeList?.length}项</span>
            </Tooltip>
          </div>
        )
      },
    },
    {
      name: 'presentDays',
      columnProps: {
        prop: 'presentDays',
        label: '赠送天数',
      },
      render: ({ row }) => `${row.presentDays}天`,
    },
    {
      name: 'operate',
      columnProps: {
        label: '操作',
        fixed: 'right',
      },
      getOperateList: ({ row }) => {
        return [{
          name: 'edit',
          label: '编辑',
          action: () => editScheme(row),
        }]
      },
      type: 'operate',
    },
  ]
})

const handleTableChange = ({ pagination: pager }) => {
  pagination.value.pageSize = pager.pageSize
  pagination.value.pageNum = pager.pageNum
  getTableData()
}

const handleSearch = () => {
  // 如果类型与已选方案的类型不同，先清空已选择的数据
  const selectedType = selectedPlans.value[0]?.rentPeriodType
  if (selectedType && rentPeriodType.value !== selectedType) {
    selectedPlans.value = []
  }
  pagination.value.pageNum = 1
  getTableData()
}

const getTableData = async () => {
  loading.value = true
  const [err, res] = await $to(
    getPlanListByGoods({
      pageNum: pagination.value.pageNum,
      pageSize: pagination.value.pageSize,
      rentPeriodType: rentPeriodType.value,
      cityCode: props.cityCode,
      modelId: props.modelId,
    }),
  )
  loading.value = false
  if (err || res?.code !== 1) return
  const { items = [], totalNum = 0 } = res.data
  tableData.value = items
  pagination.value = {
    ...pagination.value,
    total: totalNum,
  }
}

const handleClose = () => {
  rentPeriodType.value = 3
  selectedPlans.value = []
  tableData.value = []
  visible.value = false
}

const handleConfirm = () => {
  if (!selectedPlans.value.length) return
  emits('confirm', selectedPlans.value)
  handleClose()
}

const handleOpen = async () => {
  await getTableData()
}

const handleAddPlan = () => {
  const url = `/car_to_lease/leaseScheme/add?type=1&modelId=${props.modelId}&cityCode=${props.cityCode}&scene=1&brandId=${props.brandId}&seriesId=${props.seriesId}`
  const newWindow = window.open(url, '_blank')
  const currentWindow = window

  if (newWindow) {
    newWindow.addEventListener('beforeunload', () => {
      setTimeout(() => {
        currentWindow.focus()
      }, 100)
    })
  }
}

defineExpose({
  open: (initialSelectedPlans = []) => {
    selectedPlans.value = initialSelectedPlans
    // 根据已选方案的类型自动设置选择器，没有已选方案时默认月租
    rentPeriodType.value = initialSelectedPlans[0]?.rentPeriodType || 3
    visible.value = true
  },
  close: () => {
    visible.value = false
  },
})

// 新建窗口通信
const communication = ref(null)
const handleSchemeAdded = (data) => {
  if (data === true) {
    loading.value = true
    setTimeout(() => {
      pagination.value.pageNum = 1
      getTableData()
    }, 2000)
  }
}

// 编辑租赁方案按钮
const editScheme = (row) => {
  editSchemeInfo.value = {
    id: row.id,
  }
  // 跳转到个人租赁方案编辑页面
  const newWindow = window.open(`/car_to_lease/leaseScheme/edit?id=${row.id}&type=1&scene=1`, '_blank')
  const currentWindow = window

  if (newWindow) {
    newWindow.addEventListener('beforeunload', () => {
      setTimeout(() => {
        currentWindow.focus()
      }, 100)
    })
  }
}
// 更新租赁方案列表数据
const updateTableData = (list, info, planId) => {
  const index = list.value.findIndex((item) => item.id === Number(planId))
  if (index !== -1) {
    const tempData = [...list.value]
    tempData[index] = info
    list.value = tempData
  }
}
const handleSchemeEdit = (data) => {
  if (Number(data) === editSchemeInfo.value.id) {
    // 请求列表该条数据，因为租赁方案数据保存到es有延迟，所以需要等待1s
    loading.value = true
    const timer = setTimeout(async () => {
      clearTimeout(timer)
      const [err, res] = await $to(getSinglePlan({
        planId: data,
      }))
      loading.value = false
      if (!err && res?.code === 1) {
        const planSchemeInfo = {
          ...res.data,
          id: res.data.planId,
          name: res.data.planName,
          otherFeeList: (res.data.otherFees || []).map((item) => {
            return `${item.name} ${item.payDetailString}`
          }),
        }
        // 判断tableData中是否存在该条数据，如果存在更新tableData
        updateTableData(tableData, planSchemeInfo, data)
        // 判断selectedPlans中是否存在该条数据，如果存在更新selectedPlans
        updateTableData(selectedPlans, planSchemeInfo, data)
      }
    }, 1000)
  }
}

communicationHandler('lease_scheme_added', {
  lease_scheme_added: handleSchemeAdded,
  lease_scheme_edit: handleSchemeEdit,
})
</script>

<style lang="scss" scoped>
::v-deep {
  .el-alert__content {
    height: 22px;
  }
}
::v-deep .el-alert {
  padding: 7px 16px;
  margin-bottom: 12px;
}
.search-btn {
  margin-left: 12px;
}
.footer {
  margin-top: 20px;
  text-align: right;
}
.search-area {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  .search-form {
    .form-item {
      display: flex;
      align-items: center;

      .label {
        padding: 0 12px;
        height: 32px;
        line-height: 32px;
        border-radius: 6px 0 0 6px;
        background: #ffffff;
        border-width: 1px 0 1px 1px;
        border-style: solid;
        border-color: #e7e8eb;
        box-sizing: border-box;
        opacity: 1;
      }
    }
  }
  .add-plan {
    display: flex;
    align-items: center;
    font-size: 14px;

    .text {
      color: rgba(0, 0, 0, 0.6);
    }

    .action {
      padding: 0;
      margin-left: 4px;
      font-size: 14px;
      color: #366cfe;

      &:hover {
        color: #366cfe;
      }
    }
  }
}

::v-deep {
  .el-select {
    .el-input__inner {
      border-radius: 0 6px 6px 0;
      height: 32px;
      line-height: 32px;
      background: #ffffff;
      border: 1px solid #e7e8eb;
      box-sizing: border-box;
      opacity: 1;
    }
    .el-input__inner:hover,
    .el-input__inner:focus {
      border-color: #e7e8eb;
    }
  }
}
</style>
